import { newrelic } from "@jti/fe-clients";
import type {
  Config,
  PaymentSession,
  PGSClientConfig,
  PGSClientConstructor,
  PGSClientInstance,
  SecuredFieldsSessionConstructor,
} from "@jti/fe-common";
import { createPgsClientConfig, isServerSide, SinglePageCheckoutRoutes } from "@jti/fe-common";
import { getUser, PgsApi } from "@jti/rest-api";

import { pathToAbsoluteUrl } from "@checkout-v2/shared/utils/pathToAbsoluteUrl";

declare global {
  interface Window {
    __PGSClient?: PGSClientConstructor;
    __PGSSecuredFieldsSession?: SecuredFieldsSessionConstructor;
  }
}

type InitiatePaymentParams = {
  orderId: string;
  orderEntityId: string;
  paymentMethodCode: string;
  config: Config;
  isFreeTrial: boolean;
  isSubscription: boolean;
  merchant?: string;
};

type InitiatePaymentResult = {
  paymentToken: string;
};

export class PaymentGatewayError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.cause = options?.cause;
    this.name = "PaymentGatewayError";
  }
}

const pgsClientConfigFactory = (params: InitiatePaymentParams & { paymentToken: string }): PGSClientConfig => {
  const { orderId, paymentMethodCode, config, paymentToken, merchant } = params;

  const checkoutBasePath = config.CHECKOUT_PAGE_URL;

  const successUrl = pathToAbsoluteUrl(
    SinglePageCheckoutRoutes.getThankYouPagePath(orderId, paymentToken),
    checkoutBasePath
  );
  const cancelUrl = pathToAbsoluteUrl(SinglePageCheckoutRoutes.getOrderFailedPaymentPath(orderId), checkoutBasePath);

  return createPgsClientConfig(config, "pgsUrl", {
    paymentToken,
    paymentMethod: paymentMethodCode,
    successUrl,
    cancelUrl,
    errorUrl: cancelUrl,
    merchant,
  });
};

export const retrievePaymentToken = async ({
  orderEntityId,
  isFreeTrial,
  isSubscription,
}: Pick<InitiatePaymentParams, "orderEntityId" | "isFreeTrial" | "isSubscription">): Promise<string> => {
  try {
    const user = getUser();

    if (!user?.uuid) {
      throw new PaymentGatewayError("User is not authenticated");
    }

    const pgsApi = PgsApi.init();
    const paymentToken = await pgsApi.placeOrder({
      orderId: orderEntityId,
      customer: { uuid: user.uuid },
      tokenize_payment: isFreeTrial || isSubscription,
    });

    if (!paymentToken) {
      throw new PaymentGatewayError("Empty payment token returned by PGS API");
    }
    return paymentToken;
  } catch (error) {
    const gatewayError = new PaymentGatewayError("Failed to retrieve payment token", { cause: error });
    newrelic.noticeError(gatewayError);
    throw gatewayError;
  }
};

export const getPgsClientConstructor = (): PGSClientConstructor | undefined => {
  if (isServerSide()) {
    return undefined;
  }
  return window.__PGSClient;
};

export const getSecuredFieldsSessionConstructor = (): SecuredFieldsSessionConstructor | undefined => {
  if (isServerSide()) {
    return undefined;
  }
  return window.__PGSSecuredFieldsSession;
};

export const createPgsClient = (config: PGSClientConfig): PGSClientInstance => {
  const pgsConstructor = getPgsClientConstructor();

  if (!pgsConstructor) {
    const gatewayError = new PaymentGatewayError("PGS client library is not loaded");
    newrelic.noticeError(gatewayError);
    throw gatewayError;
  }

  return new pgsConstructor(config);
};

const canInitiatePaymentSession = (client: PGSClientInstance): boolean => {
  return typeof client._initPaymentSession === "function" && typeof client._pay === "function";
};

export const initiatePaymentProcess = async (params: InitiatePaymentParams): Promise<InitiatePaymentResult> => {
  const { orderEntityId, isFreeTrial, isSubscription } = params;

  const paymentToken = await retrievePaymentToken({ orderEntityId, isFreeTrial, isSubscription });
  const paymentClientConfig = pgsClientConfigFactory({ ...params, paymentToken });
  const client = createPgsClient(paymentClientConfig);

  let primaryError: unknown = null;
  let initiated = false;

  // primary path using public pay()
  if (typeof client.pay === "function") {
    try {
      await client.pay();
      initiated = true;
    } catch (err) {
      primaryError = err;
    }
  }

  // fallback path only if primary not initiated (missing pay() or pay() failure)
  if (!initiated && canInitiatePaymentSession(client)) {
    try {
      if (typeof client._initCheckout === "function") {
        await client._initCheckout();
      }

      const paymentSession = (await client._initPaymentSession()) as PaymentSession;

      if (!paymentSession?.session_id) {
        throw new PaymentGatewayError("Payment session initialization failed");
      }

      await client._pay(paymentSession);
      initiated = true;
    } catch (fallbackError) {
      const gatewayError = new PaymentGatewayError("Payment vendor redirection failed", { cause: fallbackError });
      newrelic.noticeError(gatewayError);
      throw gatewayError;
    }
  }

  if (!initiated) {
    const gatewayError = new PaymentGatewayError("No supported payment initiation method found", {
      cause: primaryError,
    });
    newrelic.noticeError(gatewayError);
    throw gatewayError;
  }

  return { paymentToken };
};

export type MountPaymentAdapterParams = {
  orderId: string;
  orderEntityId: string;
  paymentMethodCode: string;
  config: Config;
  isFreeTrial: boolean;
  isSubscription: boolean;
  merchant?: string;
  mountElement: HTMLElement;
};

export type MountPaymentAdapterResult = {
  paymentToken: string;
  unmount: () => void;
};

export const mountPaymentAdapter = async (params: MountPaymentAdapterParams): Promise<MountPaymentAdapterResult> => {
  const { mountElement, orderEntityId, isFreeTrial, isSubscription } = params;

  const paymentToken = await retrievePaymentToken({ orderEntityId, isFreeTrial, isSubscription });
  const paymentClientConfig = pgsClientConfigFactory({ ...params, paymentToken });
  const client = createPgsClient(paymentClientConfig);

  if (typeof client.mount !== "function") {
    throw new PaymentGatewayError("Payment adapter does not support mount()");
  }

  const unmount =
    client.mount(mountElement) ||
    (() => {
      mountElement.innerHTML = "";
    });

  return { paymentToken, unmount };
};
